/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import { Pressable, StyleSheet, Text, TextInput, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { KeyboardAvoidingView, type KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalFormScroll } from '../../shared/modals/ModalFormScroll'
import { ModalFormSection } from '../../shared/modals/ModalFormSection'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors'
import { typography } from '../../shared/theme/typography';
import { ticketChrome } from '../../shared/ticket/ticketChrome.styles';
import { DEFAULT_EXPENSE_CATEGORY_SLUGS, EXPENSE_CATEGORY_LABELS } from '../domain/expenseCategories'
import { expenseErpCopy } from '../domain/expenseErpCopy'
import { todayIsoDate } from '../infra/expenseListSql';
import { listExpensesForBranch } from '../infra/expensesRepoRead';
import {
  findDuplicateSupplierPayment,
  type DuplicateSupplierPaymentMatch,
} from '../application/findDuplicateSupplierPayment';
import { pickAttachmentFile, type PickedAttachmentFile } from '../../shared/attachments/infra/pickAttachmentFile'
import type { ExpenseCategorySlug } from '../domain/expenseCategories';
import { isUnexpectedFailureHandled } from '../../shared/sentry/presentUnexpectedFailure';
import { ExpenseSupplierField } from './ExpenseSupplierField';
import { ExpensePaymentFields } from './ExpensePaymentFields';
import { ExpenseFormChipRow } from './ExpenseFormChipRow';
import { ExpenseAmountInput } from './ExpenseAmountInput';
import { ExpenseFiscalSection } from './ExpenseFiscalSection';
import {
  emptyExpenseFiscalDraft,
  expenseFiscalDraftToInput,
  expenseFiscalDraftIsEmpty,
  type ExpenseFiscalDraft,
} from '../domain/expenseFiscalInvoice';
import { isFutureExpenseDate } from '../domain/expenseValidation';
import { formatExpenseDateShort } from '../domain/expenseLabels';
import { formatTurnOpenDuration } from '../../cash/app-api';
import type { ExpenseSettleMethod } from './expenseSettlementMethods';
import {
  defaultPaidDateForForm,
  expenseDateIsToday,
  getExpensePaymentSubmitBlockReason,
  needsPastExpenseCashConfirm,
  resolveSettleMethodFromPaymentForm,
  type ActiveCashSessionInfo,
  type ExpenseAlreadyPaidKind,
  type ExpensePaidFormChoice,
  type ExpensePaymentStatusChoice,
} from './expensePaymentFlow';
import { ExpensePaymentSubmitHint } from './ExpensePaymentSubmitHint';

export type { ExpenseRegisterSubmitInput } from '../screens/expenseRegisterSubmitTypes';
import type { ExpenseRegisterSubmitInput } from '../screens/expenseRegisterSubmitTypes';

export type ExpenseRegisterModalVariant = 'default' | 'cashOutflow';

type Props = {
  visible: boolean;
  submitting: boolean;
  suppliers: Array<{ id: string; name: string; taxId?: string | null }>;
  activeCashSession: ActiveCashSessionInfo | null;
  variant?: ExpenseRegisterModalVariant;
  /** Org Responsable Inscripto → muestra el bloque "Comprobante fiscal". */
  fiscalCaptureEnabled?: boolean;
  /** Sucursal activa — habilita "Consultar ARCA" en el bloque fiscal. */
  branchId?: string | null;
  onClose: () => void;
  onSubmit: (input: ExpenseRegisterSubmitInput) => void;
  onOpenCash?: () => void;
};

export function ExpenseRegisterModal({
  visible,
  submitting,
  suppliers,
  activeCashSession,
  variant = 'default',
  fiscalCaptureEnabled = false,
  branchId = null,
  onClose,
  onSubmit,
  onOpenCash,
}: Props) {
  const backdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = useWindowDimensions();
  const isMobile = winW < CONTENT_WIDE_BREAKPOINT;
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);
  const [categorySlug, setCategorySlug] = useState<ExpenseCategorySlug | null>(null);
  const [amountText, setAmountText] = useState('');
  const [description, setDescription] = useState('');
  const [receiptRef, setReceiptRef] = useState('');
  const [expenseDate, setExpenseDate] = useState(todayIsoDate());
  const [dueDate, setDueDate] = useState('');
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<ExpensePaymentStatusChoice>('unpaid');
  const [paidForm, setPaidForm] = useState<ExpensePaidFormChoice | null>(null);
  const [alreadyPaidKind, setAlreadyPaidKind] = useState<ExpenseAlreadyPaidKind | null>(null);
  const [nonDrawerMethod, setNonDrawerMethod] = useState<ExpenseSettleMethod>('transfer');
  const [settleNote, setSettleNote] = useState('');
  const [paidDate, setPaidDate] = useState(todayIsoDate());
  const [pastCashDrawerConfirmed, setPastCashDrawerConfirmed] = useState(false);
  const [attachment, setAttachment] = useState<PickedAttachmentFile | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [negativeAmountWarned, setNegativeAmountWarned] = useState(false);
  const [fiscalDraft, setFiscalDraft] = useState<ExpenseFiscalDraft>(emptyExpenseFiscalDraft);
  // Último CUIT auto-precargado desde el supplier — si el usuario no lo tocó,
  // cambiar de proveedor lo re-precarga; si lo editó, se respeta.
  const fiscalTaxIdAutoRef = useRef<string>('');
  const [duplicateMatch, setDuplicateMatch] = useState<DuplicateSupplierPaymentMatch | null>(null);

  // ── Cambios sin guardar: baseline = snapshot de los campos tras el reset de apertura.
  // Mismo patrón que AddStockModal / CatalogProductFormModal.
  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();
  const [baselineKey, setBaselineKey] = useState('');
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const activeCashSessionId = activeCashSession?.session.id ?? null;
  const isCashOutflow = variant === 'cashOutflow';

  useEffect(() => {
    if (!visible) return;
    const today = todayIsoDate();
    const resetPaymentStatus: ExpensePaymentStatusChoice = isCashOutflow ? 'paid' : 'unpaid';
    const resetPaidForm: ExpensePaidFormChoice | null = isCashOutflow
      ? activeCashSessionId
        ? 'cash_drawer_now'
        : 'transfer'
      : null;
    setCategorySlug(null);
    setAmountText('');
    setDescription('');
    setReceiptRef('');
    setExpenseDate(today);
    setDueDate('');
    setSupplierId(null);
    setPaymentStatus(resetPaymentStatus);
    setPaidForm(resetPaidForm);
    setAlreadyPaidKind(null);
    setNonDrawerMethod('transfer');
    setSettleNote('');
    setPaidDate(today);
    setPastCashDrawerConfirmed(false);
    setAttachment(null);
    setAttachmentError(null);
    setNegativeAmountWarned(false);
    setFiscalDraft(emptyExpenseFiscalDraft());
    fiscalTaxIdAutoRef.current = '';
    setDuplicateMatch(null);
    setDiscardConfirmOpen(false);
    setBaselineKey(
      JSON.stringify({
        categorySlug: null,
        amountText: '',
        description: '',
        receiptRef: '',
        expenseDate: today,
        dueDate: '',
        supplierId: null,
        paymentStatus: resetPaymentStatus,
        paidForm: resetPaidForm,
        alreadyPaidKind: null,
        nonDrawerMethod: 'transfer',
        settleNote: '',
        paidDate: today,
        pastCashDrawerConfirmed: false,
        hasAttachment: false,
        fiscalEmpty: true,
      })
    );
  }, [visible, isCashOutflow, activeCashSessionId]);

  // Pre-cargar CUIT/nombre del proveedor elegido en el bloque fiscal (snapshot
  // editable — el comprobante del proveedor es el source of truth).
  useEffect(() => {
    if (!fiscalCaptureEnabled || !supplierId) return;
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (!supplier) return;
    setFiscalDraft((d) => {
      const taxIdUntouched =
        !d.supplierTaxId.trim() || d.supplierTaxId === fiscalTaxIdAutoRef.current;
      if (!taxIdUntouched && d.supplierName.trim()) return d;
      const nextTaxId = taxIdUntouched ? (supplier.taxId ?? d.supplierTaxId) : d.supplierTaxId;
      if (taxIdUntouched) fiscalTaxIdAutoRef.current = nextTaxId;
      return {
        ...d,
        supplierTaxId: nextTaxId,
        supplierName: d.supplierName.trim() || supplier.name,
      };
    });
  }, [supplierId, suppliers, fiscalCaptureEnabled]);

  useEffect(() => {
    if (!visible || isCashOutflow || !branchId) {
      setDuplicateMatch(null);
      return;
    }
    if (categorySlug !== 'supplier_payment') {
      setDuplicateMatch(null);
      return;
    }
    const amount = parseDecimalInput(amountText, { min: 0.01 });
    if (!supplierId || amount == null) {
      setDuplicateMatch(null);
      return;
    }
    let cancelled = false;
    void listExpensesForBranch({ branchId, limit: 500 }).then((rows) => {
      if (cancelled) return;
      setDuplicateMatch(
        findDuplicateSupplierPayment({
          branchId,
          supplierId,
          amount,
          expenseDate,
          rows,
        }),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [visible, isCashOutflow, branchId, categorySlug, supplierId, amountText, expenseDate]);

  useEffect(() => {
    setPaidDate(defaultPaidDateForForm(expenseDate));
    setPastCashDrawerConfirmed(false);
  }, [expenseDate]);

  useEffect(() => {
    if (paymentStatus === 'paid' && !paidForm && activeCashSessionId) {
      setPaidForm('cash_drawer_now');
    }
  }, [paymentStatus, paidForm, activeCashSessionId]);

  const settleMethod = useMemo(
    () =>
      paymentStatus === 'paid' && paidForm
        ? resolveSettleMethodFromPaymentForm({
            paidForm,
            alreadyPaidKind,
            nonDrawerMethod,
          })
        : 'transfer',
    [paymentStatus, paidForm, alreadyPaidKind, nonDrawerMethod]
  );

  const payNow = paymentStatus === 'paid';
  const parsedAmount = parseDecimalInput(amountText, { min: 0.01 });
  const submitBlockReason = useMemo(() => {
    if (submitting) return null;
    if (categorySlug == null) return expenseErpCopy.registerSubmitBlock.chooseCategory;
    if (parsedAmount == null) return expenseErpCopy.paymentSubmitBlock.invalidAmount;
    if (payNow) {
      return getExpensePaymentSubmitBlockReason({
        paymentStatus,
        paidForm,
        alreadyPaidKind,
        settleMethod,
        activeCashSessionId,
        settleNote,
        pastCashDrawerConfirmed,
        expenseDate,
        amount: parsedAmount,
      });
    }
    return null;
  }, [
    submitting,
    categorySlug,
    parsedAmount,
    payNow,
    paymentStatus,
    paidForm,
    alreadyPaidKind,
    settleMethod,
    activeCashSessionId,
    settleNote,
    pastCashDrawerConfirmed,
    expenseDate,
  ]);
  const confirmDisabled = submitBlockReason != null;

  // P1: fecha futura — la factura queda fuera del preset por defecto (30d, tope hoy).
  // Aviso no-bloqueante; el usuario puede seguir y luego filtrar con "Todos".
  const futureDateWarning = isFutureExpenseDate(expenseDate, todayIsoDate());
  // cashOutflow: el egreso siempre sale del turno abierto (paymentStatus='paid').
  const expenseNotToday = isCashOutflow && !expenseDateIsToday(expenseDate);

  const formSnapshot = () =>
    JSON.stringify({
      categorySlug,
      amountText,
      description,
      receiptRef,
      expenseDate,
      dueDate,
      supplierId,
      paymentStatus,
      paidForm,
      alreadyPaidKind,
      nonDrawerMethod,
      settleNote,
      paidDate,
      pastCashDrawerConfirmed,
      hasAttachment: attachment != null,
      fiscalEmpty: expenseFiscalDraftIsEmpty(fiscalDraft),
    });
  const isDirty = formSnapshot() !== baselineKey;

  const handleClose = () => {
    if (submitting) return;
    // Si el confirm ya está arriba, back/Escape lo cierra a él.
    if (discardConfirmOpen) return;
    if (!isDirty) {
      onClose();
      return;
    }
    setDiscardConfirmOpen(true);
    void confirmDiscard({
      title: isCashOutflow ? 'Descartar egreso' : 'Descartar factura',
      message: 'Hay datos cargados sin guardar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
      presentation: 'overlay',
    }).then((confirmed) => {
      setDiscardConfirmOpen(false);
      if (confirmed) onClose();
    });
  };

  const handleAttachReceipt = async () => {
    setAttachmentError(null);
    try {
      const picked = await pickAttachmentFile('expenses');
      if (picked) setAttachment(picked);
    } catch (e) {
      // Unexpected: pickAttachmentFile already toasted + reported.
      if (isUnexpectedFailureHandled(e)) return;
      setAttachmentError(e instanceof Error ? e.message : String(e));
    }
  };

  const formTitle = isCashOutflow ? 'Registrar egreso' : expenseErpCopy.newBillTitle;
  const formKicker = isCashOutflow ? 'Gastos · Caja' : 'Gastos';
  const formHint = isCashOutflow ? 'Sale de la caja de este turno.' : 'Cargala ahora, pagala cuando quieras.';
  const primaryActionLabel = submitting
    ? 'Guardando…'
    : payNow
      ? expenseErpCopy.actionRegisterAndPay
      : isCashOutflow
        ? 'Registrar egreso'
        : 'Guardar factura';

  // Desktop: grilla 2-col en Datos y Monto (proto gastos-ux-v1, ~860px).
  // Mobile: columna única igual que antes.
  const fieldCol = !isMobile ? styles.fieldCol : undefined;

  const categoryField = (
    <ModalFormField label="Categoría" required>
      <ExpenseFormChipRow
        options={DEFAULT_EXPENSE_CATEGORY_SLUGS.map((slug) => ({
          id: slug,
          label: EXPENSE_CATEGORY_LABELS[slug],
        }))}
        value={categorySlug}
        onChange={setCategorySlug}
        disabled={submitting}
        testIDPrefix="gastos.new.category"
      />
      {categorySlug === 'supplier_payment' && !isCashOutflow ? (
        <Text style={styles.fieldWarning} testID="gastos.new.supplierPaymentHint">
          {expenseErpCopy.supplierPaymentMerchandiseHint}
        </Text>
      ) : null}
    </ModalFormField>
  );

  const duplicateBanner =
    duplicateMatch && !isCashOutflow ? (
      <View style={modalExperience.warningPanel} testID="gastos.new.duplicatePurchaseWarning">
        <Text style={posUi.checkoutHint}>
          {duplicateMatch.kind === 'purchase_linked'
            ? expenseErpCopy.duplicatePurchaseLinkedBanner
            : expenseErpCopy.duplicateSimilarManualBanner}
        </Text>
      </View>
    ) : null;

  const amountField = (
    <ModalFormField label="Monto" required>
      <ExpenseAmountInput
        value={amountText}
        onChangeText={setAmountText}
        onNegativeAttempt={() => setNegativeAmountWarned(true)}
        placeholder="0"
        style={modalExperience.formInput}
        placeholderTextColor={ticketColors.ink3}
        editable={!submitting}
        testID="gastos.new.amount"
        accessibilityLabel={isCashOutflow ? 'Monto del egreso' : 'Monto del gasto'}
      />
      {negativeAmountWarned ? (
        <Text style={styles.fieldWarning} testID="gastos.new.negativeAmountHint">
          {expenseErpCopy.negativeAmountHint}
        </Text>
      ) : null}
    </ModalFormField>
  );

  // Proto v-extra: el egreso de caja es una sola sección slim —
  // Fecha | Monto · Categoría · Nota · info-box del turno.
  const formFieldsElement = isCashOutflow ? (
    <ModalFormSection title="Datos del egreso">
      <View style={!isMobile ? styles.fieldsRow : undefined}>
        <View style={fieldCol}>
          <ModalFormField label="Fecha del egreso" required>
            <InventoryIsoDateField
              value={expenseDate}
              onChange={setExpenseDate}
              emptyLabel="Seleccionar fecha"
              ariaLabel="Fecha del egreso"
              showClearButton={false}
              iosEmptyBehavior="seedToday"
            />
            {futureDateWarning ? (
              <Text style={styles.fieldWarning} testID="gastos.new.futureDateWarning">
                {expenseErpCopy.futureDateWarning}
              </Text>
            ) : null}
          </ModalFormField>
        </View>
        <View style={fieldCol}>{amountField}</View>
      </View>

      {categoryField}

      <ModalFormField label="Nota">
        <TextInput
          style={modalExperience.formInput}
          value={description}
          onChangeText={setDescription}
          placeholder="Detalle opcional"
          placeholderTextColor={ticketColors.ink3}
          editable={!submitting}
          testID="gastos.new.description"
          accessibilityLabel="Nota del egreso"
        />
      </ModalFormField>

      {activeCashSession ? (
        <View style={modalExperience.infoPanel}>
          <Text style={styles.infoTitle}>Turno activo</Text>
          <Text style={styles.infoText}>
            {activeCashSession.operatorLabel ?? 'Operador'} ·{' '}
            {formatTurnOpenDuration(activeCashSession.session.openedAt).toLowerCase()}
          </Text>
          <Text style={styles.infoText}>Sale de la caja de este turno.</Text>
        </View>
      ) : null}
      {expenseNotToday && activeCashSession ? (
        <View style={modalExperience.warningPanel}>
          <Text style={posUi.checkoutHint}>
            El egreso queda con fecha {formatExpenseDateShort(expenseDate)}. La plata sale de la caja de hoy (
            {formatExpenseDateShort(todayIsoDate())}).
          </Text>
          {needsPastExpenseCashConfirm(expenseDate) ? (
            !pastCashDrawerConfirmed ? (
              <TouchableOpacity
                style={styles.confirmLinkWrap}
                onPress={() => setPastCashDrawerConfirmed(true)}
                disabled={submitting}
                accessibilityRole="button"
              >
                <Text style={styles.link}>Confirmo: la plata sale de la caja hoy</Text>
              </TouchableOpacity>
            ) : (
              <Text style={styles.confirmedText}>Confirmado: sale de la caja de hoy.</Text>
            )
          ) : null}
        </View>
      ) : null}
    </ModalFormSection>
  ) : (
    <>
      <ModalFormSection title="Datos de la factura">
        <View style={!isMobile ? styles.fieldsRow : undefined}>
          <View style={fieldCol}>
            <ModalFormField label="Fecha de la factura" required>
              <InventoryIsoDateField
                value={expenseDate}
                onChange={setExpenseDate}
                emptyLabel="Seleccionar fecha"
                ariaLabel="Fecha del gasto"
                showClearButton={false}
                iosEmptyBehavior="seedToday"
              />
              {futureDateWarning ? (
                <Text style={styles.fieldWarning} testID="gastos.new.futureDateWarning">
                  {expenseErpCopy.futureDateWarning}
                </Text>
              ) : null}
            </ModalFormField>
          </View>
          <View style={fieldCol}>
            <ModalFormField label="Vencimiento">
              <InventoryIsoDateField
                value={dueDate}
                onChange={setDueDate}
                emptyLabel="Sin vencimiento"
                ariaLabel="Fecha de vencimiento"
                showClearButton={true}
              />
            </ModalFormField>
          </View>
        </View>

        {categoryField}

        <ExpenseSupplierField
          suppliers={suppliers}
          supplierId={supplierId}
          onSupplierIdChange={setSupplierId}
          submitting={submitting}
        />
      </ModalFormSection>

      <ModalFormSection title="Monto">
        <View style={!isMobile ? styles.fieldsRow : undefined}>
          <View style={fieldCol}>{amountField}</View>
          <View style={fieldCol}>
            <ModalFormField label="N° de comprobante">
              <TextInput
                style={modalExperience.formInput}
                value={receiptRef}
                onChangeText={setReceiptRef}
                placeholder="Opcional — ej. A-0042"
                placeholderTextColor={ticketColors.ink3}
                editable={!submitting}
              />
            </ModalFormField>
          </View>
        </View>

        <ModalFormField label="Descripción">
          <TextInput
            style={modalExperience.formInput}
            value={description}
            onChangeText={setDescription}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            editable={!submitting}
            testID="gastos.new.description"
            accessibilityLabel="Descripción del gasto"
          />
        </ModalFormField>

        <ModalFormField label={expenseErpCopy.attachmentLabel}>
          {attachment ? (
            <View style={styles.attachRow}>
              <Ionicons name="document-text-outline" size={20} color={ticketColors.ink2} />
              <Text style={styles.attachFileName} numberOfLines={1}>
                {attachment.fileName}
              </Text>
              <TouchableOpacity
                onPress={() => {
                  setAttachment(null);
                  setAttachmentError(null);
                }}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel={expenseErpCopy.attachmentRemoveLabel}
                style={styles.attachRemoveBtn}
              >
                <Text style={styles.attachRemoveText}>{expenseErpCopy.attachmentRemoveLabel}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              style={styles.attachCta}
              onPress={handleAttachReceipt}
              disabled={submitting}
              accessibilityRole="button"
              accessibilityLabel={expenseErpCopy.attachmentAddLabel}
            >
              <Ionicons name="attach-outline" size={18} color={ticketColors.gold} />
              <Text style={styles.attachCtaText}>{expenseErpCopy.attachmentAddLabel}</Text>
            </TouchableOpacity>
          )}
          {attachmentError ? <Text style={styles.attachError}>{attachmentError}</Text> : null}
        </ModalFormField>
      </ModalFormSection>

      {fiscalCaptureEnabled ? (
        <ExpenseFiscalSection
          draft={fiscalDraft}
          onChange={setFiscalDraft}
          expenseAmount={parsedAmount}
          submitting={submitting}
          branchId={branchId}
        />
      ) : null}

      <ExpensePaymentFields
        expenseDate={expenseDate}
        paymentStatus={paymentStatus}
        onPaymentStatusChange={setPaymentStatus}
        paidForm={paidForm}
        onPaidFormChange={setPaidForm}
        alreadyPaidKind={alreadyPaidKind}
        onAlreadyPaidKindChange={setAlreadyPaidKind}
        nonDrawerMethod={nonDrawerMethod}
        onNonDrawerMethodChange={setNonDrawerMethod}
        settleNote={settleNote}
        onSettleNoteChange={setSettleNote}
        paidDate={paidDate}
        onPaidDateChange={setPaidDate}
        activeCashSession={activeCashSession}
        pastCashDrawerConfirmed={pastCashDrawerConfirmed}
        onPastCashDrawerConfirmedChange={setPastCashDrawerConfirmed}
        submitting={submitting}
        onOpenCash={onOpenCash}
        testIDPrefix="gastos.new"
      />
    </>
  );

  const handlePrimaryAction = () => {
    if (categorySlug == null) return;
    onSubmit({
      categorySlug,
      amountText,
      description,
      receiptRef,
      supplierId,
      expenseDate,
      dueDate: dueDate || null,
      attachment: attachment
        ? { fileName: attachment.fileName, mimeType: attachment.mimeType, base64: attachment.base64, fileSize: attachment.fileSize }
        : null,
      paymentIntent: paymentStatus === 'paid' ? 'pay_now' : 'unpaid',
      settleMethod,
      settleNote,
      paidDate,
      fiscal: fiscalCaptureEnabled ? expenseFiscalDraftToInput(fiscalDraft) : null,
    });
  };

  // ── Mobile: bottom sheet (View shell — Pressable envolviendo el card bloquea scroll) ──
  if (isMobile) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose} accessibilityViewIsModal>
          <View style={ticketChrome.sheetBackdrop}>
            <Pressable
              style={StyleSheet.absoluteFill}
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            />
            <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap} automaticOffset>
            <View style={[ticketChrome.sheetCard, { maxHeight: winH * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
              <View style={ticketChrome.grabber} />

              <View style={ticketChrome.sheetHeader}>
                <View style={styles.sheetHeaderText}>
                  <Text style={ticketChrome.sheetSubtitle}>{formTitle}</Text>
                  {formHint ? (
                    <Text style={styles.sheetHint} numberOfLines={2}>
                      {formHint}
                    </Text>
                  ) : null}
                </View>
                <TouchableOpacity
                  onPress={handleClose}
                  style={ticketChrome.sheetCloseBtn}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
                </TouchableOpacity>
              </View>

              <ModalAwareScrollView
                ref={scrollRef}
                style={styles.sheetScroll}
                contentContainerStyle={styles.sheetScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
                bottomOffset={spacing.sm}
              >
                <View ref={scrollContentRef} collapsable={false}>
                  {duplicateBanner}
                  {formFieldsElement}
                </View>
              </ModalAwareScrollView>

              <View style={ticketChrome.foot}>
                <View style={{ width: '100%', gap: spacing.xs }}>
                  <ExpensePaymentSubmitHint message={submitBlockReason} />
                  <View style={{ flexDirection: 'row', gap: spacing.xs, width: '100%' }}>
                    <TouchableOpacity
                      style={[ticketChrome.btnGhost, { flex: 1, backgroundColor: ticketColors.paper2 }]}
                      onPress={handleClose}
                      disabled={submitting}
                      accessibilityRole="button"
                      accessibilityLabel="Cancelar"
                    >
                      <Text style={[ticketChrome.btnGhostText, { color: ticketColors.ink }]}>Cancelar</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[ticketChrome.btnGold, { flex: 1 }, confirmDisabled && { opacity: 0.4 }]}
                      onPress={handlePrimaryAction}
                      disabled={confirmDisabled}
                      accessibilityRole="button"
                      accessibilityLabel={primaryActionLabel}
                      testID="gastos.new.save"
                    >
                      <Text style={ticketChrome.btnGoldText}>{primaryActionLabel}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </View>
            </KeyboardAvoidingView>
            {DiscardConfirmModal}
          </View>
      </Modal>
    );
  }

  // ── Wide: centered card modal ──
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
      accessibilityViewIsModal
    >
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            modalExperience.modalWidthBill,
            { gap: spacing.sm, width: '100%', alignSelf: 'center', maxHeight: '96%' },
          ]}
        >
          <ModalScreenHeader
            kicker={formKicker}
            title={formTitle}
            subtitle={formHint}
            onClose={handleClose}
          />

          <ModalFormScroll
            ref={scrollRef}
            contentRef={scrollContentRef}
            contentContainerStyle={modalExperience.formPanel}
            keyboardBottomPadding={spacing.sm}
          >
            {duplicateBanner}
            {formFieldsElement}
          </ModalFormScroll>

          <View style={{ width: '100%', gap: spacing.xs }}>
            <ExpensePaymentSubmitHint message={submitBlockReason} />
            <View style={[posUi.modalActions, modalExperience.footerActions]}>
              <TouchableOpacity
                style={[posUi.secondaryButton, posUi.modalButtonHalf]}
                onPress={handleClose}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Cancelar"
              >
                <Text style={posUi.secondaryButtonText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[posUi.primaryButton, posUi.modalButtonHalf, confirmDisabled && posUi.disabledCard]}
                disabled={confirmDisabled}
                onPress={handlePrimaryAction}
                accessibilityRole="button"
                accessibilityLabel={primaryActionLabel}
                testID="gastos.new.save"
              >
                <Text style={posUi.primaryButtonText}>{primaryActionLabel}</Text>
              </TouchableOpacity>
            </View>
          </View>
          {DiscardConfirmModal}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  infoTitle: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
    marginBottom: spacing.xs,
  },
  infoText: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  link: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
    marginTop: spacing.xs,
  },
  confirmLinkWrap: {
    marginTop: spacing.xs,
  },
  confirmedText: {
    ...typography.caption,
    color: ticketColors.ok,
    marginTop: spacing.xs,
    fontWeight: '600',
  },
  fieldsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  fieldCol: {
    flexGrow: 1,
    flexBasis: '45%',
    minWidth: 220,
  },
  sheetHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  sheetHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
    marginTop: 2,
  },
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.md,
  },
  fieldWarning: {
    ...typography.caption,
    color: ticketColors.warn,
    marginTop: spacing.xs,
  },
  attachCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    minHeight: 44,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderStyle: 'dashed',
    backgroundColor: ticketColors.paper,
  },
  attachCtaText: {
    ...typography.body,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  attachRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 44,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  attachFileName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
    flex: 1,
    minWidth: 0,
  },
  attachRemoveBtn: {
    flexShrink: 0,
    paddingVertical: spacing.xs,
  },
  attachRemoveText: {
    ...typography.caption,
    color: ticketColors.bad,
    fontWeight: '700',
  },
  attachError: {
    color: ticketColors.bad,
    fontSize: 12,
    marginTop: 4,
  },
});
